/**
 * External Dependencies
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import cn from "classnames";
import _ from "lodash";

/**
 * Internal dependencies:
 */
import { createApi } from "@/frontend/domain/cart/local-storage-api";
import { getDetails } from "@/frontend/domain/cart/ajax";
import CartRow from "@/frontend/domain/cart/components/cart-row";
import route from "@/utils/route";
import { useDiscount } from "@/frontend/domain/cart/use-discount";
import { showToastMessage } from "@/frontend/modules/add-to-cart";

const storageApi = createApi();

const findItemIndex = (item, items) => {
    return items.findIndex((_item) => _item.productId === item.productId);
};

const MIN_TRANSACTION_AMOUNT = 0.5;

const Cart = () => {
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [allItemsHaveDelivery, setAllItemsHaveDelivery] = useState(false);
    const [summary, setSummary] = useState({
        subTotal: 0,
        tax: 0,
        shipping: 0,
        total: 0,
        handlingFee: 0,
    });

    const updateAllItemsHaveDelivery = (_items) => {
        for (const item of _items) {
            if (!item?.deliveryId) {
                setAllItemsHaveDelivery(false);
                return;
            }
        }

        setAllItemsHaveDelivery(true);
    };

    const updateSummary = useCallback(async () => {
        const res = await getDetails();

        if (res.success) {
            setSummary(
                _.pick(res.data, [
                    "subTotal",
                    "tax",
                    "shipping",
                    "total",
                    "discount",
                    "handlingFee",
                ]),
            );
        } else {
            displayError(res);
        }

        setLoading(false);
    }, [setLoading]);

    const { discount, onDiscountInput, updateDiscount } = useDiscount({
        onApply: updateSummary,
        storageApi,
    });

    const onDeliveryChange = useCallback(
        async (item, enabled, deliveryId) => {
            const _items = [...items];
            const index = findItemIndex(item, _items);

            if (index === -1) {
                return;
            }

            if (enabled) {
                await storageApi.setItemDelivery(item.productId, deliveryId);
            } else {
                await storageApi.removeItemDelivery(item.productId);
            }

            item.deliveryId = deliveryId;
            setItems(_items);
            updateAllItemsHaveDelivery(_items);
            updateSummary();
        },
        [updateSummary, items],
    );

    const displayError = useCallback((res) => {
        const error = res?.errors?.at(0) ?? res.error;

        console.log(error);
        showToastMessage(5000, error);
    }, []);

    const removeItemByProductId = useCallback(
        async (productId) => {
            const index = items.findIndex(
                (item) => item.productId === productId,
            );

            if (index !== -1) {
                await storageApi.removeAllItemsWithId(productId);
                const newItems = items.slice();
                newItems.splice(index, 1);
                setItems(newItems);
                await updateSummary();
            }
        },
        [items, updateSummary],
    );

    const onItemDelete = useCallback(
        ({ productId }) => {
            removeItemByProductId(productId);
        },
        [removeItemByProductId],
    );

    const onItemQuantityChange = useCallback(
        async (quantity, item) => {
            const index = findItemIndex(item, items);
            if (quantity < 1 || index === -1) {
                return;
            }

            const res = await storageApi.setItemCount(item.productId, quantity);

            if (!res.success) {
                displayError(res);
            }

            const newQuantity = res.success ? quantity : res.meta.limit;

            setItems([
                ...items.slice(0, index),
                {
                    ...item,
                    count: newQuantity,
                },
                ...items.slice(index + 1),
            ]);

            updateSummary();
        },
        [findItemIndex, items],
    );

    useEffect(() => {
        (async () => {
            const details = await getDetails();

            if (details.success) {
                const common = Object.values(details.data.groupedItems).flat();

                setItems(common);
                setSummary({
                    subTotal: details.data.subTotal,
                    tax: details.data.tax,
                    shipping: details.data.shipping,
                    total: details.data.total,
                    discount: details.data.discount,
                    handlingFee: details.data.handlingFee,
                });
                updateAllItemsHaveDelivery(common);
            } else {
                displayError(details);
            }
        })();
    }, []);

    useEffect(() => {
        updateSummary();
    }, [discount.discount]);

    const isBelowMinTransactionAmount = useMemo(() => {
        return (
            parseFloat(summary?.total) !== 0 &&
            summary?.subTotal < MIN_TRANSACTION_AMOUNT
        );
    }, [summary]);

    const allowCheckout = useMemo(
        () => allItemsHaveDelivery && !isBelowMinTransactionAmount,
        [allItemsHaveDelivery, isBelowMinTransactionAmount],
    );

    const onCheckoutClick = (event) => {
        if (!allowCheckout) {
            event.preventDefault();
        }

        if (!allItemsHaveDelivery) {
            showToastMessage(
                5000,
                "Please select a delivery method for all items in the cart.",
            );
        }
    };

    if (!items || !items.length) {
        return (
            <section className={cn("section-cart", { loading })}>
                <div className="shell">
                    <div className="section__inner">
                        <div className="section__head">
                            <h3>Shopping Cart</h3>
                        </div>

                        <div className="section__body">
                            <div className="cart">
                                <div className="cart__empty">
                                    Your cart is empty
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className={cn("section-cart", { loading: loading })}>
            <div className="shell">
                <div className="section__inner">
                    <div className="section__head">
                        <h3>Shopping Cart</h3>
                    </div>

                    <div className="section__body">
                        <div className="cart">
                            <div className="cart__table">
                                <div className="cart__table-head">
                                    <div className="cart__row cart__row-head">
                                        <div className="cart__col size--2of5">
                                            <div className="cart__head">
                                                <h6>Item</h6>
                                            </div>
                                        </div>

                                        <div className="cart__col size--1of5">
                                            <div className="cart__head">
                                                <h6>Quantity</h6>
                                            </div>
                                        </div>

                                        <div className="cart__col size--1of5">
                                            <div className="cart__head">
                                                <h6>Delivery</h6>
                                            </div>
                                        </div>

                                        <div className="cart__col size--1of5">
                                            <div className="cart__head">
                                                <h6>Price</h6>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="cart__table-body">
                                    {items.map((item) => {
                                        return (
                                            <CartRow
                                                item={item}
                                                key={item.productId}
                                                onDelete={onItemDelete}
                                                onQuantityChange={
                                                    onItemQuantityChange
                                                }
                                                onDeliveryChange={
                                                    onDeliveryChange
                                                }
                                            />
                                        );
                                    })}
                                </div>

                                <div className="cart__table-foot">
                                    <div className="cart__row">
                                        <div className="cart__card">
                                            <div className="cart__card-head">
                                                <h5>Have a Promotion Code?</h5>

                                                <p>
                                                    If you have a promotion
                                                    code, enter it here. (only
                                                    one promotion code per
                                                    order)
                                                </p>
                                            </div>

                                            <div className="cart__card-actions">
                                                <ul>
                                                    <li>
                                                        <label
                                                            htmlFor="discountCode"
                                                            className="hidden"
                                                        >
                                                            Promo Code
                                                        </label>

                                                        <div className="cart__controls">
                                                            <input
                                                                type="text"
                                                                className="field field--alt cart__field-code"
                                                                name="discountCode"
                                                                value={
                                                                    discount.code ??
                                                                    ""
                                                                }
                                                                onChange={
                                                                    onDiscountInput
                                                                }
                                                                placeholder="XXX-XXX-XXXX"
                                                            />
                                                        </div>
                                                    </li>

                                                    <li className="actions__add-code">
                                                        <button
                                                            className="btn btn--block btn--promo"
                                                            onClick={
                                                                updateDiscount
                                                            }
                                                        >
                                                            Add Promotion Code
                                                        </button>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="cart__collaterals">
                                <div className="cart__total">
                                    <ul>
                                        <li>
                                            <p className="cart__total-text">
                                                Subtotal
                                            </p>

                                            <p className="cart__total-amount cart__total-subtotal">
                                                ${summary.subTotal}
                                            </p>
                                        </li>
                                        {Number(summary.shipping) > 0 ? (
                                            <li>
                                                <p className="cart__total-text">
                                                    Delivery
                                                </p>

                                                <p className="cart__total-amount cart__total-tax">
                                                    ${summary.shipping}
                                                </p>
                                            </li>
                                        ) : null}
                                        {Number(summary.handlingFee) > 0 ? (
                                            <li>
                                                <p className="cart__total-text">
                                                    Handling Fee
                                                </p>

                                                <p className="cart__total-amount cart__total-tax">
                                                    ${summary.handlingFee}
                                                </p>
                                            </li>
                                        ) : null}
                                        {Number(summary.tax) > 0 ? (
                                            <li>
                                                <p className="cart__total-text">
                                                    Tax
                                                </p>

                                                <p className="cart__total-amount cart__total-tax">
                                                    ${summary.tax}
                                                </p>
                                            </li>
                                        ) : null}
                                        {discount.name &&
                                            discount.discount == 0 && (
                                                <li>
                                                    <p className="cart__total-text">
                                                        {discount.name}
                                                    </p>

                                                    <p className="cart__total-amount cart__total-promo">
                                                        ({discount.discount}%)
                                                        -$
                                                        {summary.discount}
                                                    </p>
                                                </li>
                                            )}
                                        <li>
                                            <p className="cart__total-text--large cart__total-text">
                                                Total
                                            </p>

                                            <p className="cart__total-amount cart__total-total">
                                                ${summary.total}
                                            </p>
                                        </li>

                                        {isBelowMinTransactionAmount && (
                                            <li>
                                                <p className="cart__total-text warning">
                                                    Notice
                                                </p>

                                                <p className="cart__total-amount cart__total-subtotal">
                                                    The minimum order amount is
                                                    {" $"}
                                                    {MIN_TRANSACTION_AMOUNT.toFixed(
                                                        2,
                                                    )}
                                                    .
                                                </p>
                                            </li>
                                        )}
                                    </ul>
                                </div>

                                <div className="cart__actions">
                                    <ul>
                                        <li>
                                            <a
                                                onClick={onCheckoutClick}
                                                href={
                                                    allowCheckout
                                                        ? route("checkout.show")
                                                        : "#"
                                                }
                                                className="btn btn--yellow"
                                                disabled={!allowCheckout}
                                            >
                                                Proceed to Checkout
                                            </a>
                                        </li>

                                        <li>
                                            <a
                                                href="/"
                                                className="btn btn--transparent"
                                            >
                                                Continue Shopping
                                            </a>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Cart;
